import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { PencilIcon, TrashIcon } from '@/components/catalog/icons';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime, pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type { Note, NoteCreateInput, NoteListParams, TaskTargetType } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  MAX_BODY,
  bylineOf,
  deleteRefusalMessage,
  editRefusalMessage,
  writeMessage,
} from './note-rules';

/*
 * The notes timeline for one record.
 *
 * Self-contained: it fetches its own page, and add / edit / delete all happen
 * in place. A note is a single text field, so a modal for it would be three
 * clicks of ceremony around one paragraph — the composer sits at the top of the
 * list and an edit turns the note itself into a textarea.
 *
 * The permission rules are the interesting part and they are NOT symmetric.
 * Edit is the author alone; delete is the author OR an audit holder. Both are
 * commented where they are enforced, because the asymmetry looks like a bug
 * until you know why it is there.
 */

const PER_PAGE = 25;

/*
 * `MAX_BODY`, `bylineOf`, `SYSTEM_AUTHOR`, `SYSTEM_NOTE_IMMUTABLE`,
 * `writeMessage` and the two refusal sentences moved to `./note-rules` on
 * 2026-10-16, verbatim, so the company page's Notes section reads the same
 * copy. `NoteRow`, `NotesSkeleton`, `NoteIcon` and `NoteDeleteDialog` are
 * exported from this file for the same reason. Nothing about this panel's
 * behaviour changed.
 */

/**
 * Past this, a note is collapsed with an explicit control to open it.
 *
 * Not a scroll box and not a hard clip: a note can run to 20,000 characters and
 * one of those would otherwise be the entire panel, burying the six one-liners
 * underneath it. Collapsed is always paired with "Show more", so nothing is
 * ever cut off with no way back to it.
 */
const LONG_NOTE_CHARS = 420;

/* --- target plumbing ----------------------------------------------------- */

interface NoteTarget {
  type: TaskTargetType;
  id: number;
}

/*
 * A switch rather than a computed key, so the five target ids stay type-checked
 * against the params they belong to. `{ [param]: id }` widens to a string index
 * and would happily send a misspelled key into a strict query that 422s on it.
 */
function listParamsFor(target: NoteTarget): NoteListParams {
  switch (target.type) {
    case 'deal':
      return { dealId: target.id };
    case 'quote':
      return { quoteId: target.id };
    case 'invoice':
      return { invoiceId: target.id };
    case 'deliverable':
      return { deliverableId: target.id };
    case 'company':
      return { companyId: target.id };
  }
}

function createInputFor(target: NoteTarget, body: string): NoteCreateInput {
  switch (target.type) {
    case 'deal':
      return { body, dealId: target.id };
    case 'quote':
      return { body, quoteId: target.id };
    case 'invoice':
      return { body, invoiceId: target.id };
    case 'deliverable':
      return { body, deliverableId: target.id };
    case 'company':
      return { body, companyId: target.id };
  }
}

/* --- messages ------------------------------------------------------------ */

function loadMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) return 'You do not have permission to read notes on this record.';
    if (cause.status === 404) return 'This record no longer exists.';
    return cause.message;
  }
  return 'Could not load notes. Check your connection and try again.';
}

/* --- pieces -------------------------------------------------------------- */

export function NoteIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
      <path d="M14 3v6h6" />
      <path d="M8 13h7" />
      <path d="M8 17h4" />
    </svg>
  );
}

function ErrorBlock({ children }: { children: string }) {
  return (
    <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
      <span className="min-w-0">{children}</span>
    </Notice>
  );
}

/** The loading state in the shape of the notes it becomes: avatar, byline, body. */
export function NotesSkeleton() {
  return (
    <ul className="min-w-0">
      {[0, 1, 2].map((row) => (
        <li key={row} className="flex gap-2.5 border-t border-border px-3 py-2.5 first:border-t-0">
          <Skeleton className="h-6 w-6 rounded-full" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-2 h-3.5 w-full" />
            <Skeleton className="mt-1.5 h-3.5 w-4/5" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* --- one note ------------------------------------------------------------ */

export function NoteRow({
  note,
  context,
  viewerId,
  canUpdate,
  canDelete,
  busy,
  error,
  editing,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRequestDelete,
}: {
  note: Note;
  /**
   * An optional line under the byline naming where the note sits — the company
   * page's Notes section passes "Deal · <name>" for a note on one of the
   * account's documents. The drawer passes nothing, so it renders as before.
   */
  context?: ReactNode;
  viewerId: number | null;
  /**
   * `notes.update` / `notes.delete` from the caller's `ability` — the
   * permission set, which `/notes` really enforces.
   *
   * ⚠️ ANDed with the author rules below, never a replacement for them: one is
   * per-account and one is per-record, and neither can express the other.
   */
  canUpdate: boolean;
  canDelete: boolean;
  busy: boolean;
  error: string | null;
  editing: boolean;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  onStartEdit: (note: Note) => void;
  onCancelEdit: () => void;
  onSaveEdit: (note: Note) => void;
  onRequestDelete: (note: Note) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  /*
   * 🔴 **`author === null` IS TWO FACTS AND `system` IS THE DISCRIMINATOR.**
   * *SUPERSEDED: "`author` is null when that user was deleted."* — true when
   * written, and the whole reason the flag exists.
   *
   * A note EMERGE wrote itself and a note whose author has since been deleted
   * both arrive with `author: null`, and they want opposite bylines: one is a
   * record of something NOBODY did, the other is a person's statement that has
   * lost its name. Reading the null alone files the first under the second and
   * prints "Former colleague" over a machine-written note — a confident wrong
   * answer, with nothing failing.
   *
   * Both still render in full. A note is a statement that was made about this
   * record, so it gets a name that says what happened rather than a blank
   * byline; `system` only decides WHICH name.
   */
  const authorName = bylineOf(note).label;

  /*
   * EDIT — the author, and only the author.
   *
   * The audit grant deliberately does NOT reach here: an administrator editing
   * a note would republish somebody else's statement under that person's name,
   * which misattributes it. Someone who disagrees adds their own note. The API
   * answers a non-author with a 403 whoever they are, so offering the control
   * on an author match is the whole rule, not an approximation of it.
   */
  const mayEdit = !note.system && canUpdate && viewerId !== null && note.author?.id === viewerId;

  /*
   * DELETE — the author OR an audit holder. Not the same test as edit.
   *
   * The audit's two powers are asymmetric on purpose: it may remove a note, it
   * may not rewrite one. Whether this viewer holds that grant is not knowable
   * from a note, so the button is offered on an author match and a 403 is
   * caught and shown in the confirm dialog. An audit holder therefore does not
   * see a control they would in fact be allowed to use — the safe direction.
   */
  const mayDelete = !note.system && canDelete && viewerId !== null && note.author?.id === viewerId;

  /*
   * ⚠️ **THE `!note.system` ON BOTH TESTS ABOVE LOOKS REDUNDANT AND IS NOT.**
   *
   * A system note carries `author: null`, so `note.author?.id === viewerId` is
   * `undefined === number` and already false — the controls would be withheld
   * today with the flag unread. **That is a coincidence of how the server
   * happens to store the note, not a rule about what may be written**, and it
   * is the proxy-field shape exactly: a null author answers *whose statement is
   * this*, never *may this be edited*. The day a system note is given a service
   * account to write under — the obvious next step for anything that needs an
   * actor id — both controls reappear on a record the API refuses to everybody,
   * and nothing here would fail.
   *
   * ⛔ So the test names the real rule. Do not "simplify" it back out.
   */

  const longBody = note.body.length > LONG_NOTE_CHARS;

  return (
    <li className="min-w-0 border-t border-border px-3 py-2.5 first:border-t-0">
      <div className="flex min-w-0 items-start gap-2.5">
        <Avatar name={authorName} size="sm" className="mt-0.5" />

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            {/*
              * THREE bylines, not two. `note.author ? … : …` was a two-way
              * branch over what is now a three-way fact, and its false arm —
              * muted italic — is the SINCE-DELETED-COLLEAGUE treatment. Letting
              * a system note fall into it would dress "nobody wrote this" in
              * the styling that means "a person wrote this and their name is
              * gone", re-conflating the exact pair the flag exists to separate,
              * one line after separating it.
              *
              * A system note therefore keeps FULL WEIGHT: it is not a
              * degraded byline, it is a correct and complete one.
              */}
            <span
              className={cx(
                'min-w-0 text-xs font-medium [overflow-wrap:anywhere]',
                note.system || note.author ? 'text-fg' : 'text-fg-muted italic',
              )}
            >
              {authorName}
            </span>
            {/*
              * ── WHY A SYSTEM NOTE LOOKS DIFFERENT BEYOND ITS BYLINE ────────
              *
              * Decided rather than defaulted. A byline alone does not carry
              * this, because the author slot on every other row holds a
              * PERSON'S NAME — so a reader scanning the timeline reads
              * "SEED EMERGE" as one more name and never stops to consider that
              * this row is a record of something nobody did. The wordmark
              * answers *who*; it does not answer *what kind of thing is this*.
              *
              * ⛔ **This is NOT helper text and the prohibition is not being
              * carved out.** That rule forbids a SENTENCE attached to a
              * CONTROL, where the prose competes with the control it sits
              * beside and rots against it. This is a one-word LABEL on the
              * RECORD, the same class as the `edited` marker three lines below
              * — which this panel already keeps, for the same reason — and as
              * the "retired" label on a picker option. It explains no control
              * and instructs nobody.
              *
              * ⚠️ **A muted or tinted treatment was the alternative and was
              * REFUSED, on two grounds.** The muted step is taken, by the
              * deleted-author byline directly above. And colour is never the
              * only signal in this app, so a tint would have needed a word
              * beside it regardless — at which point it is this, with a tint.
              *
              * `neutral` deliberately: a system note is not a status, not a
              * warning and not something to act on. `accent` is rationed to
              * primary actions and active state, and `info`/`warning` would
              * make an ordinary bookkeeping record read as needing attention.
              *
              * ⚠️ **"Automatic", NOT "System" — the word is measured, not
              * chosen for taste.** `<Badge>System</Badge>` ALREADY EXISTS in
              * this app, on `/permission-sets`, where it means a seeded set
              * that cannot be deleted (`OriginBadge`). Reusing it would give
              * one word two meanings on two screens for the same reader.
              * "Automatic" also says the thing a reader needs — nobody wrote
              * it — where "System" says only that software is involved.
              */}
            {note.system && <Badge tone="neutral">Automatic</Badge>}
            <span className="text-2xs text-fg-subtle tabular">
              {formatDateTime(note.createdAt)}
            </span>
            {/*
             * `edited` is imperfect in exactly ONE direction: an edit landing in
             * the same second as the insert reads as unedited. It is never
             * wrong the other way. So the marker is rendered when it is true and
             * there is deliberately NO "never edited" branch — the inverse is
             * not a fact the field can support, and building on it is the
             * mistake already made once here with `modifiedBy` on deliverables.
             */}
            {note.edited && (
              <span
                className="text-2xs text-fg-subtle"
                title={`Last edited ${formatDateTime(note.updatedAt)}`}
              >
                edited
              </span>
            )}
          </div>

          {context !== undefined && context !== null && (
            <div className="mt-0.5 min-w-0 text-2xs text-fg-subtle [overflow-wrap:anywhere]">
              {context}
            </div>
          )}

          {editing ? (
            <div className="mt-1.5">
              <Textarea
                value={editDraft}
                onChange={(event) => onEditDraftChange(event.target.value)}
                maxLength={MAX_BODY}
                rows={4}
                disabled={busy}
                aria-label="Edit note"
                autoFocus
              />
              {/*
               * Only the body is editable. Moving a note to another record is
               * deliberately not offered anywhere: a note is a statement about
               * one record at one time, and re-pointing it rewrites that
               * history with nothing left to show it was ever said elsewhere.
               */}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => onSaveEdit(note)}
                  loading={busy}
                  disabled={editDraft.trim() === '' || editDraft === note.body}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancelEdit} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p
                className={cx(
                  'mt-1 min-w-0 text-sm whitespace-pre-wrap text-fg-secondary [overflow-wrap:anywhere]',
                  longBody && !expanded && 'line-clamp-6',
                )}
              >
                {note.body}
              </p>
              {longBody && (
                <button
                  type="button"
                  onClick={() => setExpanded((open) => !open)}
                  className="mt-1 rounded-xs text-2xs font-medium text-fg-secondary underline-offset-2 hover:text-fg hover:underline"
                >
                  {expanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </>
          )}

          {error && (
            <p role="alert" className="mt-1.5 text-2xs text-danger [overflow-wrap:anywhere]">
              {error}
            </p>
          )}
        </div>

        {!editing && (mayEdit || mayDelete) && (
          <div className="flex shrink-0 items-center gap-0.5">
            {mayEdit && (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leadingIcon={<PencilIcon />}
                onClick={() => onStartEdit(note)}
                disabled={busy}
                aria-label={`Edit note by ${authorName}`}
              />
            )}
            {mayDelete && (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leadingIcon={<TrashIcon />}
                onClick={() => onRequestDelete(note)}
                disabled={busy}
                aria-label={`Delete note by ${authorName}`}
              />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/* --- the panel ----------------------------------------------------------- */

export interface NotesPanelProps {
  target: { type: TaskTargetType; id: number };
  /** The signed-in user's id — decides who may edit. */
  viewerId: number | null;
  /**
   * Fires after a note is created, edited or deleted and the server ACCEPTED
   * it — never on a refusal — so a host can refresh what it shows about the
   * record. Optional; omitted, nothing is told.
   */
  onChanged?: () => void;
}

export function NotesPanel({ target, viewerId, onChanged }: NotesPanelProps): ReactElement {
  const targetType = target.type;
  const targetId = target.id;
  const targetKey = `${targetType}:${targetId}`;

  /*
   * Self-gating, like the drawer that mounts it. Three separate grants — a set
   * can grant `notes.create` and refuse `notes.delete`.
   *
   * 🔴 See the composer below for the reasoning this replaces.
   */
  const { ability } = useAuth();
  const canCreateNote = ability.canCreate('notes');
  const canUpdateNote = ability.canUpdate('notes');
  const canDeleteNote = ability.canDelete('notes');
  const createNoteDenial = canCreateNote ? null : denialReason(ability, 'create', 'notes');

  /*
   * Every piece of state below is TAGGED with the target it belongs to, and the
   * three view states are derived from those tags rather than set.
   *
   * That is what lets the panel switch records without an effect that fires
   * setState on the way in: when `target` changes, `loaded.key` no longer
   * matches and the panel is loading again by definition. An in-flight edit or
   * a row-level error from the previous record cannot leak onto a same-numbered
   * note on the next one either, because their keys no longer match.
   */
  const [loaded, setLoaded] = useState<{
    key: string;
    notes: Note[];
    total: number;
    page: number;
  } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editing, setEditing] = useState<{ key: string; id: number; draft: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ key: string; id: number; message: string } | null>(
    null,
  );

  const [pendingDelete, setPendingDelete] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  /** The in-flight "show older" request, so it can be abandoned on unmount. */
  const moreRef = useRef<AbortController | null>(null);

  const current = loaded?.key === targetKey ? loaded : null;
  const notes = current?.notes ?? [];
  const total = current?.total ?? 0;
  const page = current?.page ?? 1;
  const loadError = failure?.key === targetKey ? failure.message : null;
  const loading = current === null && loadError === null;

  useEffect(() => {
    const controller = new AbortController();
    const key = `${targetType}:${targetId}`;

    api
      .listNotes(
        // `order` is left to the server, which defaults to desc. Newest first is
        // the only order a timeline is read in.
        { ...listParamsFor({ type: targetType, id: targetId }), page: 1, perPage: PER_PAGE },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setLoaded({ key, notes: response.data, total: response.meta.total, page: 1 });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setFailure({ key, message: loadMessage(cause) });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoadingMore(false);
        setMoreError(null);
      });

    return () => {
      controller.abort();
      moreRef.current?.abort();
    };
  }, [targetType, targetId, reloadToken]);

  useEffect(() => () => moreRef.current?.abort(), []);

  /** Replaces the loaded page, but only while it still describes this record. */
  function patchLoaded(change: (notes: Note[], total: number) => { notes: Note[]; total: number }) {
    setLoaded((previous) => {
      if (!previous || previous.key !== targetKey) return previous;
      const next = change(previous.notes, previous.total);
      return { ...previous, notes: next.notes, total: next.total };
    });
  }

  function loadMore() {
    moreRef.current?.abort();
    const controller = new AbortController();
    moreRef.current = controller;
    const key = targetKey;
    const nextPage = page + 1;

    setLoadingMore(true);
    setMoreError(null);

    api
      .listNotes(
        { ...listParamsFor({ type: targetType, id: targetId }), page: nextPage, perPage: PER_PAGE },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setLoaded((previous) =>
          previous && previous.key === key
            ? {
                ...previous,
                notes: [...previous.notes, ...response.data],
                total: response.meta.total,
                page: nextPage,
              }
            : previous,
        );
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setMoreError(loadMessage(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMore(false);
      });
  }

  function addNote() {
    const body = draft.trim();
    if (body === '' || adding) return;

    setAdding(true);
    setAddError(null);

    api
      .createNote(createInputFor({ type: targetType, id: targetId }, body))
      .then((response) => {
        // Newest first, so a new note goes on top rather than costing a refetch.
        patchLoaded((items, count) => ({ notes: [response.data, ...items], total: count + 1 }));
        setDraft('');
        onChanged?.();
      })
      .catch((cause: unknown) => {
        setAddError(writeMessage(cause, 'Could not save this note. Try again.'));
      })
      .finally(() => setAdding(false));
  }

  function saveEdit(note: Note) {
    const body = editing?.draft.trim() ?? '';
    if (body === '' || busyId !== null) return;

    const key = targetKey;
    setBusyId(note.id);
    setRowError(null);

    api
      // Only `body` — moving a note between records is deliberately not offered.
      .updateNote(note.id, { body })
      .then((response) => {
        patchLoaded((items, count) => ({
          notes: items.map((item) => (item.id === note.id ? response.data : item)),
          total: count,
        }));
        setEditing(null);
        onChanged?.();
      })
      .catch((cause: unknown) => {
        // Two 403s that route to opposite places — see `editRefusalMessage`.
        setRowError({ key, id: note.id, message: editRefusalMessage(cause) });
      })
      .finally(() => setBusyId(null));
  }

  function confirmDelete() {
    const note = pendingDelete;
    if (!note || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    api
      .deleteNote(note.id)
      .then(() => {
        patchLoaded((items, count) => ({
          notes: items.filter((item) => item.id !== note.id),
          total: Math.max(0, count - 1),
        }));
        setPendingDelete(null);
        onChanged?.();
      })
      .catch((cause: unknown) => {
        // Author OR audit holder; the grant is not readable from a note, so a
        // refusal is surfaced rather than pre-empted — see `deleteRefusalMessage`.
        setDeleteError(deleteRefusalMessage(cause));
      })
      .finally(() => setDeleting(false));
  }

  const remaining = total - notes.length;

  /*
   * ── HOW MANY OF THESE SEED EMERGE WROTE ITSELF ──────────────────────────────
   *
   * 🔴 **The defect this closes.** `/notes/summary` stopped counting system
   * notes on 2026-09-14; `GET /notes` still LISTS them, to every reader. So the
   * trigger that opens this drawer could read `2 notes` over this panel headed
   * `3 notes, newest first`, with an `Automatic`-badged row visible in between —
   * two true figures about two different populations, and nothing erroring.
   *
   * ⛔ **COUNTED, NEVER SUBTRACTED.** The standing rule at
   * `NoteSummaryRow.systemCount`: the summary's human count is VIEWER-SCOPED
   * while system notes are visible to everybody, so any arithmetic between the
   * two crosses two visibility rules and is wrong by however many notes a
   * colleague happens to have written. This figure is a direct count of
   * `note.system` over rows already in hand — one population, one request, no
   * arithmetic across a boundary. Nothing here reads `/notes/summary`, and
   * nothing here may start deriving one figure from the other.
   *
   * ⚠️ **`total` STAYS THE PRIMARY FIGURE and the split is a parenthetical.** A
   * panel header describes the list beneath it, and a reader can count the rows:
   * making the headline exclude system notes would put `2 notes` over three
   * visible rows, which is the same defect one surface along. `(1 automatic)`
   * says *of which*, where a `·` would claim two peer figures.
   *
   * ⚠️ **Only when the WHOLE list is loaded.** `remaining > 0` means pages are
   * still unfetched, so a count off the loaded rows would be a figure about the
   * first page presented as a figure about the record — short, plausible, and
   * silent. A partial count is worse than none, so the header falls back to
   * exactly what it said before. `PER_PAGE` is 25, so the split renders on the
   * ordinary record and stands down only on a long timeline.
   *
   * ⚠️ **Until `Note.system` is on the wire this is 0 on every row**, so the
   * description below is byte-for-byte today's.
   */
  const systemShown = notes.filter((note) => note.system).length;
  const wholeListShown = remaining <= 0;
  const notesDescription =
    systemShown > 0 && wholeListShown
      ? `${pluralise(total, 'note')} (${systemShown} automatic), newest first`
      : `${pluralise(total, 'note')}, newest first`;

  return (
    <Panel className="min-w-0">
      <PanelHeader
        id="notes-panel-heading"
        title="Notes"
        {...(loading ? {} : { description: notesDescription })}
      />

      {/*
       * 🔴 **CORRECTED 2026-09-04.** *SUPERSEDED: "The composer is never gated
       * on a role. These routes carry no role guard — anyone who can see the
       * record, a viewer included, may add a note, and hiding the box would
       * invent a restriction the API does not have."*
       *
       * **True premise, wrong conclusion.** `notes` is one of the three modules
       * this API enforces through PERMISSION SETS rather than roles, so "no
       * role guard" is exactly what makes the set the whole answer — the grant
       * is real, it narrows, and a set without `notes.create` answered every
       * add with a 403 while the box sat there inviting one.
       *
       * The still-true half is why this is DISABLED and not removed: the
       * restriction is not the API's absence of one, it is this reader's
       * account, and it is named rather than left to be guessed at.
       */}
      <PanelBody className="min-w-0 border-b border-border">
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={MAX_BODY}
          rows={3}
          disabled={adding || !canCreateNote}
          placeholder="Add a note…"
          aria-label="New note"
        />
        <div className="mt-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 text-2xs text-fg-subtle">
            {createNoteDenial
              ? createNoteDenial
              : draft.length > MAX_BODY - 500
                ? `${(MAX_BODY - draft.length).toLocaleString()} characters left`
                : 'Visible to everyone who can see this record.'}
          </span>
          <Button
            size="sm"
            variant="primary"
            onClick={addNote}
            loading={adding}
            disabled={!canCreateNote || draft.trim() === ''}
          >
            Add note
          </Button>
        </div>
        {addError && (
          <div className="mt-2">
            <ErrorBlock>{addError}</ErrorBlock>
          </div>
        )}
      </PanelBody>

      <PanelBody density="flush" className="min-w-0">
        {loading ? (
          <NotesSkeleton />
        ) : loadError ? (
          <div className="flex flex-col items-start gap-2 p-3">
            <ErrorBlock>{loadError}</ErrorBlock>
            <Button
              size="sm"
              onClick={() => {
                setFailure(null);
                setReloadToken((token) => token + 1);
              }}
            >
              Try again
            </Button>
          </div>
        ) : notes.length === 0 ? (
          <EmptyState
            icon={<NoteIcon />}
            title="No notes yet"
            description="Add the first note on this record."
          />
        ) : (
          <>
            <ul className="min-w-0">
              {notes.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  viewerId={viewerId}
                  canUpdate={canUpdateNote}
                  canDelete={canDeleteNote}
                  busy={busyId === note.id}
                  error={
                    rowError && rowError.key === targetKey && rowError.id === note.id
                      ? rowError.message
                      : null
                  }
                  editing={editing?.key === targetKey && editing.id === note.id}
                  editDraft={editing?.id === note.id ? editing.draft : note.body}
                  onEditDraftChange={(value) =>
                    setEditing((previous) => (previous ? { ...previous, draft: value } : previous))
                  }
                  onStartEdit={(subject) => {
                    setEditing({ key: targetKey, id: subject.id, draft: subject.body });
                    setRowError(null);
                  }}
                  onCancelEdit={() => {
                    setEditing(null);
                    setRowError(null);
                  }}
                  onSaveEdit={saveEdit}
                  onRequestDelete={(subject) => {
                    setPendingDelete(subject);
                    setDeleteError(null);
                  }}
                />
              ))}
            </ul>

            {remaining > 0 && (
              <div className="flex flex-col items-center gap-2 border-t border-border px-3 py-2.5">
                <Button size="sm" onClick={loadMore} loading={loadingMore}>
                  Show {Math.min(remaining, PER_PAGE)} older
                </Button>
                {moreError && <ErrorBlock>{moreError}</ErrorBlock>}
              </div>
            )}
          </>
        )}
      </PanelBody>

      <NoteDeleteDialog
        note={pendingDelete}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
        deleting={deleting}
        error={deleteError}
      />
    </Panel>
  );
}

/* --- the delete confirmation, shared with the company Notes section ------ */

export function NoteDeleteDialog({
  note,
  onClose,
  onConfirm,
  deleting,
  error,
}: {
  /** The note being confirmed; `null` is closed. */
  note: Note | null;
  onClose: () => void;
  onConfirm: () => void;
  deleting: boolean;
  error: string | null;
}) {
  return (
    <ConfirmDialog
      open={note !== null}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Delete this note?"
      description={
        note ? (
          <>
            {/*
              * `by <byline>` through the same helper the row uses, so the
              * dialog cannot name somebody the row did not. It reads "by
              * SEED EMERGE" on a system note — which is unreachable from
              * here today, because the control that opens this dialog is
              * withheld on one — and that is the point: a bundle older than
              * the server, or a note whose flag arrives after this list was
              * fetched, must not produce a dialog offering to delete a
              * machine-written record "by a former colleague". The 403 below
              * is the guarantee; this is only the wording.
              */}
            The note by {bylineOf(note).inSentence} from{' '}
            {formatDateTime(note.createdAt)} will be deleted and can't be recovered.
            <span className="mt-2 block max-h-24 overflow-y-auto rounded-md border border-border bg-surface-sunken px-2 py-1.5 text-xs whitespace-pre-wrap text-fg-secondary [overflow-wrap:anywhere]">
              {note.body.slice(0, 300)}
              {note.body.length > 300 ? '…' : ''}
            </span>
          </>
        ) : null
      }
      confirmLabel="Delete note"
      loading={deleting}
      {...(error ? { error } : {})}
    />
  );
}
